import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getStripePlan, stripeCheckoutLink } from '../../redux/actions/myAccountAction'
import { membershipNameType } from '../../utils/constant'
import { formatMoney } from '../../utils/currency'
import { formatDate, formatNumber } from '../../i18n/format'
import { APRIL_STANDARD_PROMO, getAprilStandardPromoTimeLeft } from '../../utils/aprilStandardPromo'
import './unesco-standard-banner.css'

// A translation key: the error is translated at render, so it follows a language switch.
const CHECKOUT_ERROR_MESSAGE = 'offers:promo.checkoutUnavailable'

// The yearly prices are whole US dollars: '$90', not '$90.00'.
const wholeUsd = (amount: number) =>
	formatNumber(amount, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })

const UNESCOStandardBanner: React.FC = () => {
	const { t } = useTranslation('offers')
	const videoRef = useRef<HTMLVideoElement>(null)
	const [timeLeft, setTimeLeft] = useState(getAprilStandardPromoTimeLeft())
	const [loading, setLoading] = useState(false)
	const [standardYearlyPriceId, setStandardYearlyPriceId] = useState('')
	const [error, setError] = useState('')

	useEffect(() => {
		const timer = setInterval(() => setTimeLeft(getAprilStandardPromoTimeLeft()), 1000)
		return () => clearInterval(timer)
	}, [])

	useEffect(() => {
		let isMounted = true

		const loadStandardYearlyPriceId = async () => {
			const plans = await getStripePlan()
			if (!isMounted) return

			const yearlyPlans = Array.isArray(plans?.yearly) ? plans.yearly : []
			const standardPlan = yearlyPlans.find((plan: any) => plan.value === membershipNameType.Standard)

			if (standardPlan?.priceId) {
				setStandardYearlyPriceId(standardPlan.priceId)
				setError('')
				return
			}

			setError(CHECKOUT_ERROR_MESSAGE)
		}

		loadStandardYearlyPriceId().catch(() => {
			if (!isMounted) return
			setError(CHECKOUT_ERROR_MESSAGE)
		})

		return () => {
			isMounted = false
		}
	}, [])

	const handleVideoReady = () => {
		videoRef.current?.play().catch(() => undefined)
	}

	const handleClaim = async (event: React.MouseEvent) => {
		event.stopPropagation()

		if (loading || !standardYearlyPriceId) return

		setLoading(true)
		setError('')

		try {
			const affiliateId =
				typeof window !== 'undefined' && (window as any).affiliateId ? String((window as any).affiliateId) : undefined

			const response: any = await stripeCheckoutLink({
				priceId: standardYearlyPriceId,
				couponCode: APRIL_STANDARD_PROMO.couponCode,
				...(affiliateId && { affiliateId }),
			})

			if (response?.return_url) {
				window.open(response.return_url, '_self')
				return
			}

			if (response?.url) {
				window.open(response.url, '_self')
				return
			}

			setError(CHECKOUT_ERROR_MESSAGE)
		} catch {
			setError(CHECKOUT_ERROR_MESSAGE)
		} finally {
			setLoading(false)
		}
	}

	const pad = (value: number) => String(value).padStart(2, '0')
	const isExpired = !Object.values(timeLeft).some(Boolean)

	if (isExpired) {
		return null
	}

	return (
		<div className='unesco-banner'>
			<video
				ref={videoRef}
				className='unesco-banner-video'
				autoPlay
				loop
				muted
				playsInline
				preload='auto'
				onCanPlay={handleVideoReady}
				onLoadedData={handleVideoReady}
			>
				<source src='/videos/unesco-chinese-language-day.mp4' type='video/mp4' />
			</video>
			<div className='unesco-banner-overlay' />

			<div className='unesco-banner-content'>
				<div className='unesco-banner-left'>
					<div className='unesco-banner-text'>
						<div className='unesco-banner-eyebrow'>{t('unesco.eyebrow')}</div>
						<h5 className='unesco-banner-title'>{t('unesco.title')}</h5>
						<div className='unesco-banner-pricing'>
							<div className='unesco-banner-pricing-line'>
								<span className='unesco-banner-price-strike'>{formatMoney(APRIL_STANDARD_PROMO.regularMonthlyPrice, 'USD')}</span>
								<span className='unesco-banner-price-new'>{formatMoney(APRIL_STANDARD_PROMO.promoMonthlyEquivalent, 'USD')}</span>
								<span className='unesco-banner-price-period'>{t('promo.perMonth')}</span>
							</div>
							<div className='unesco-banner-billing-note'>
								<span className='unesco-banner-billing-old'>{wholeUsd(APRIL_STANDARD_PROMO.regularYearlyPrice)}</span>
								<span className='unesco-banner-billing-current'>{wholeUsd(APRIL_STANDARD_PROMO.promoYearlyPrice)}</span>
								<span>{t('unesco.chargedYearly')}</span>
							</div>
						</div>
						<p className='unesco-banner-subtitle'>
							{t('unesco.subtitle', {
								date: formatDate(APRIL_STANDARD_PROMO.endDate, 'monthDay', { timeZone: 'Asia/Hong_Kong' }),
							})}
						</p>
					</div>
				</div>

				<div className='unesco-banner-right'>
					<div className='unesco-banner-countdown'>
						<div className='unesco-banner-countdown-unit'>
							<span className='unesco-banner-countdown-num'>{pad(timeLeft.days)}</span>
							<span className='unesco-banner-countdown-label'>{t('promo.countdown.days', { count: timeLeft.days })}</span>
						</div>
						<span className='unesco-banner-countdown-sep'>:</span>
						<div className='unesco-banner-countdown-unit'>
							<span className='unesco-banner-countdown-num'>{pad(timeLeft.hours)}</span>
							<span className='unesco-banner-countdown-label'>{t('promo.countdown.hours', { count: timeLeft.hours })}</span>
						</div>
						<span className='unesco-banner-countdown-sep'>:</span>
						<div className='unesco-banner-countdown-unit'>
							<span className='unesco-banner-countdown-num'>{pad(timeLeft.minutes)}</span>
							<span className='unesco-banner-countdown-label'>
								{t('promo.countdown.minutes', { count: timeLeft.minutes })}
							</span>
						</div>
						<span className='unesco-banner-countdown-sep'>:</span>
						<div className='unesco-banner-countdown-unit'>
							<span className='unesco-banner-countdown-num'>{pad(timeLeft.seconds)}</span>
							<span className='unesco-banner-countdown-label'>
								{t('promo.countdown.seconds', { count: timeLeft.seconds })}
							</span>
						</div>
					</div>
					<button className='unesco-banner-cta' onClick={handleClaim} disabled={loading || !standardYearlyPriceId}>
						{loading
							? t('promo.processing')
							: standardYearlyPriceId
							? t('unesco.cta', { price: wholeUsd(APRIL_STANDARD_PROMO.promoYearlyPrice) })
							: t('promo.loadingOffer')}
					</button>
					{error && <div className='unesco-banner-error'>{t(error)}</div>}
				</div>
			</div>
		</div>
	)
}

export default UNESCOStandardBanner
